// Modal de alta/edición rápida de persona.
// Prompt 0460 — alineado con PersonaDatosTab: tipoContratoId/categoriaLaboralId (FK),
// tipoCosto, centroCostesId, generoId, departamentoId.
// Campos derivados (supervisor, organización, país, centro principal) NO se editan aquí —
// el drawer /people/ los muestra solo-lectura porque vienen del organigrama/centros.

import { useState, useEffect } from 'react';
import {
  crearPersona,
  actualizarPersona,
  fetchDepartamentos,
  fetchTiposContrato,
  fetchCategoriasLaborales,
  fetchProyectos,
  fetchPreviewCodigoPersona,
  request,
  type PersonaResumen,
} from '../../../../shared/services/api';
import { useI18n } from '../../../../../i18n';

interface AdminPersonaModalProps {
  persona: PersonaResumen | null;
  onClose: () => void;
  onSaved: () => void;
}

interface SelectOption {
  value: string;
  label: string;
}

export function AdminPersonaModal({ persona, onClose, onSaved }: AdminPersonaModalProps) {
  const { t } = useI18n();
  const adm = (t.structure as Record<string, unknown>)?.admin as
    | Record<string, unknown>
    | undefined;
  const lbl = (adm ?? {}) as Record<string, string>;
  const pf = t.people.form;
  const isEdit = persona !== null;

  const [form, setForm] = useState<Record<string, string | undefined>>(() => {
    if (!persona) return { tipoPersona: 'EMPLEADO', estado: 'ACTIVO' };
    const pr = persona as unknown as Record<string, unknown>;
    const str = (k: string) => (pr[k] as string | undefined) ?? undefined;
    return {
      codigo: persona.codigo,
      nif: str('nif'),
      nombre: persona.nombre,
      apellidos: persona.apellidos,
      nombreMostrar: str('nombreMostrar'),
      email: persona.email,
      emailSecundario: str('emailSecundario'),
      telefono: persona.telefono ?? undefined,
      telefonoMovil: str('telefonoMovil'),
      tipoPersona: persona.tipoPersona,
      estado: persona.estado,
      tipoContratoId: str('tipoContratoId'),
      categoriaLaboralId: str('categoriaLaboralId'),
      departamentoId: str('departamentoId'),
      tipoCosto: str('tipoCosto'),
      centroCostesId: str('centroCostesId'),
      fechaAlta: (str('fechaAlta') ?? '').slice(0, 10) || undefined,
      fechaBaja: (str('fechaBaja') ?? '').slice(0, 10) || undefined,
      fechaNacimiento: (str('fechaNacimiento') ?? '').slice(0, 10) || undefined,
      generoId: str('generoId'),
      observaciones: str('observaciones'),
    };
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Lookup data — mismos selects que PersonaDatosTab
  const [departamentos, setDepartamentos] = useState<SelectOption[]>([]);
  const [tiposContrato, setTiposContrato] = useState<SelectOption[]>([]);
  const [categoriasLaborales, setCategoriasLaborales] = useState<SelectOption[]>([]);
  const [proyectos, setProyectos] = useState<SelectOption[]>([]);
  const [generos, setGeneros] = useState<SelectOption[]>([]);

  useEffect(() => {
    fetchDepartamentos({ porPagina: '100' })
      .then((r) => setDepartamentos((r.data ?? []).map((d) => ({ value: d.id, label: d.nombre }))))
      .catch(() => {
        /* ignore */
      });

    fetchTiposContrato()
      .then((r) =>
        setTiposContrato((r.data ?? []).map((tc) => ({ value: tc.id, label: tc.nombre }))),
      )
      .catch(() => {
        /* ignore */
      });

    fetchCategoriasLaborales()
      .then((r) =>
        setCategoriasLaborales((r.data ?? []).map((cl) => ({ value: cl.id, label: cl.nombre }))),
      )
      .catch(() => {
        /* ignore */
      });

    fetchProyectos({ porPagina: '100', estado: 'ACTIVO' })
      .then((r) =>
        setProyectos(
          (r.data ?? []).map((pr) => ({
            value: pr.id,
            label: `${pr.codigo} — ${pr.nombre}`,
          })),
        ),
      )
      .catch(() => {
        /* ignore */
      });

    request<{ data: Array<{ id: string; nombre: string }> }>('/catalogos-tenant?tipo=GENERO')
      .then((r) => setGeneros((r.data ?? []).map((g) => ({ value: g.id, label: g.nombre }))))
      .catch(() => {
        /* ignore */
      });
  }, []);

  // Preview del proximo codigo en modo alta — se recalcula al cambiar tipoPersona.
  useEffect(() => {
    if (isEdit) return;
    const tipo = (form.tipoPersona ?? 'EMPLEADO') as 'EMPLEADO' | 'COLABORADOR' | 'EXTERNO';
    let cancelled = false;
    fetchPreviewCodigoPersona(tipo)
      .then((preview) => {
        if (cancelled) return;
        setForm((prev) => ({ ...prev, codigo: preview.proximoCodigo }));
      })
      .catch(() => {
        // silencioso: si el endpoint falla no rompe el formulario
      });
    return () => {
      cancelled = true;
    };
  }, [isEdit, form.tipoPersona]);

  const set = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError('');
  };

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleSave = async () => {
    if (!form.nombre?.trim() || !form.apellidos?.trim()) {
      setError(lbl.requiredError ?? 'Nombre y apellidos son obligatorios');
      return;
    }
    if (!form.tipoPersona) {
      setError(lbl.requiredTipo ?? 'Tipo de persona es obligatorio');
      return;
    }
    if (!form.email?.trim()) {
      setError(t.people.validation.required);
      return;
    }
    if (!EMAIL_RE.test(form.email)) {
      setError(t.people.validation.emailFormat);
      return;
    }

    try {
      setSaving(true);
      setError('');
      const body: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(form)) {
        // codigo es fuente de verdad backend (autogenerado)
        if (k === 'codigo') continue;
        if (v !== undefined && v !== '') body[k] = v;
      }

      if (isEdit) {
        await actualizarPersona(persona.id, body);
      } else {
        await crearPersona(body as Parameters<typeof crearPersona>[0]);
      }
      onSaved();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al guardar';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 12,
          borderTop: '3px solid #1F3864',
          maxWidth: 760,
          width: '95%',
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: '24px 28px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
      >
        {/* Title */}
        <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#0E243E' }}>
          {isEdit ? (lbl.editTitle ?? 'Editar persona') : (lbl.createTitle ?? 'Nueva persona')}
        </h2>

        {/* Section 1: Identificacion */}
        <SectionHeader label={lbl.secIdentificacion ?? 'Identificacion'} />
        <div style={gridStyle}>
          <Field
            label={lbl.codigo ?? 'Codigo'}
            value={form.codigo}
            onChange={(v) => set('codigo', v)}
            readOnly
            helper={isEdit ? '(autogenerado)' : '(preview)'}
          />
          <Field label={lbl.nif ?? 'NIF'} value={form.nif} onChange={(v) => set('nif', v)} />
          <Field
            label={lbl.nombre ?? 'Nombre'}
            required
            value={form.nombre}
            onChange={(v) => set('nombre', v)}
          />
          <Field
            label={lbl.apellidos ?? 'Apellidos'}
            required
            value={form.apellidos}
            onChange={(v) => set('apellidos', v)}
          />
          <Field
            label={lbl.nombreMostrar ?? 'Nombre a mostrar'}
            value={form.nombreMostrar}
            onChange={(v) => set('nombreMostrar', v)}
            colSpan={2}
          />
        </div>

        {/* Section 2: Contacto */}
        <SectionHeader label={lbl.secContacto ?? 'Contacto'} />
        <div style={gridStyle}>
          <Field
            label={lbl.email ?? 'Email'}
            type="email"
            value={form.email}
            onChange={(v) => set('email', v)}
            readOnly
            helper={lbl.emailHelperGestionAdmin ?? 'Se gestiona desde Admin > Usuarios'}
          />
          <Field
            label={lbl.emailSecundario ?? 'Email secundario'}
            type="email"
            value={form.emailSecundario}
            onChange={(v) => set('emailSecundario', v)}
          />
          <Field
            label={lbl.telefono ?? 'Telefono'}
            value={form.telefono}
            onChange={(v) => set('telefono', v)}
          />
          <Field
            label={lbl.telefonoMovil ?? 'Telefono movil'}
            value={form.telefonoMovil}
            onChange={(v) => set('telefonoMovil', v)}
          />
        </div>

        {/* Section 3: Datos laborales */}
        <SectionHeader label={lbl.secLaboral ?? 'Datos laborales'} />
        <div style={gridStyle3}>
          <SelectField
            label={lbl.tipoPersona ?? 'Tipo persona'}
            required
            value={form.tipoPersona}
            options={[
              { value: 'EMPLEADO', label: (adm?.tipoPersonaEmpleado as string) ?? 'Empleado' },
              {
                value: 'COLABORADOR',
                label: (adm?.tipoPersonaColaborador as string) ?? 'Colaborador',
              },
              { value: 'EXTERNO', label: (adm?.tipoPersonaExterno as string) ?? 'Externo' },
            ]}
            onChange={(v) => set('tipoPersona', v)}
          />
          {isEdit && (
            <SelectField
              label={lbl.estado ?? 'Estado'}
              value={form.estado}
              options={[
                { value: 'ACTIVO', label: (adm?.estadoActivo as string) ?? 'Activo' },
                { value: 'BAJA', label: (adm?.estadoBaja as string) ?? 'Baja' },
                { value: 'SUSPENDIDO', label: (adm?.estadoSuspendido as string) ?? 'Suspendido' },
              ]}
              onChange={(v) => set('estado', v)}
            />
          )}
          <SelectField
            label={pf.contractType}
            value={form.tipoContratoId}
            options={tiposContrato}
            onChange={(v) => set('tipoContratoId', v)}
          />
          <SelectField
            label={pf.jobCategory}
            value={form.categoriaLaboralId}
            options={categoriasLaborales}
            onChange={(v) => set('categoriaLaboralId', v)}
          />
          <SelectField
            label={pf.department}
            value={form.departamentoId}
            options={departamentos}
            onChange={(v) => set('departamentoId', v)}
          />
          <SelectField
            label={pf.tipoCosto}
            value={form.tipoCosto}
            options={[
              {
                value: 'OPERACIONES',
                label: (adm?.tipoCostoOperaciones as string) ?? 'Operaciones',
              },
              {
                value: 'ESTRUCTURA_PROYECTO',
                label: (adm?.tipoCostoEstructuraProyecto as string) ?? 'Estructura proyecto',
              },
              {
                value: 'ESTRUCTURA_CORPORATIVA',
                label: (adm?.tipoCostoEstructuraCorporativa as string) ?? 'Estructura corporativa',
              },
            ]}
            onChange={(v) => set('tipoCosto', v)}
          />
          <SelectField
            label={pf.centroCostes}
            value={form.centroCostesId}
            options={proyectos}
            onChange={(v) => set('centroCostesId', v)}
          />
          <Field
            label={pf.hireDate}
            type="date"
            value={form.fechaAlta}
            onChange={(v) => set('fechaAlta', v)}
          />
          <Field
            label={pf.terminationDate}
            type="date"
            value={form.fechaBaja}
            onChange={(v) => set('fechaBaja', v)}
          />
          <Field
            label={pf.birthDate}
            type="date"
            value={form.fechaNacimiento}
            onChange={(v) => set('fechaNacimiento', v)}
          />
          <SelectField
            label={pf.gender}
            value={form.generoId}
            options={generos}
            onChange={(v) => set('generoId', v)}
          />
        </div>

        {/* Section 4: Observaciones */}
        <SectionHeader label={lbl.secObservaciones ?? 'Observaciones'} />
        <div style={{ marginBottom: 20 }}>
          <textarea
            value={form.observaciones ?? ''}
            onChange={(e) => set('observaciones', e.target.value)}
            rows={3}
            style={{
              ...inputStyle,
              resize: 'vertical',
              minHeight: 60,
            }}
          />
        </div>

        {/* Error */}
        {error && (
          <div
            style={{
              padding: '8px 12px',
              borderRadius: 6,
              marginBottom: 12,
              background: '#FEF2F2',
              color: '#DC2626',
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px',
              background: 'transparent',
              border: '1px solid #D1D5DB',
              borderRadius: 6,
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
              color: '#374151',
            }}
          >
            {lbl.cancel ?? 'Cancelar'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '8px 24px',
              background: '#2F5496',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
              fontFamily: 'inherit',
            }}
          >
            {saving ? (lbl.saving ?? 'Guardando...') : (lbl.save ?? 'Guardar')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────

function SectionHeader({ label }: { label: string }) {
  return (
    <h3
      style={{
        margin: '20px 0 10px',
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: '#1F3864',
        borderBottom: '1px solid #E5E7EB',
        paddingBottom: 6,
      }}
    >
      {label}
    </h3>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '8px 10px',
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  fontSize: 13,
  width: '100%',
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: '#5A6171',
  marginBottom: 4,
  display: 'block',
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '12px 16px',
  marginBottom: 4,
};

const gridStyle3: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  gap: '12px 16px',
  marginBottom: 4,
};

interface FieldProps {
  label: string;
  value?: string;
  onChange: (v: string) => void;
  required?: boolean;
  type?: string;
  colSpan?: number;
  readOnly?: boolean;
  helper?: string;
}

function Field({
  label,
  value,
  onChange,
  required,
  type = 'text',
  colSpan,
  readOnly,
  helper,
}: FieldProps) {
  return (
    <div style={colSpan === 2 ? { gridColumn: '1 / -1' } : undefined}>
      <label style={labelStyle}>
        {label}
        {required && <span style={{ color: '#DC2626' }}> *</span>}
        {helper && (
          <span style={{ fontSize: 10, color: '#9CA3AF', marginLeft: 6, fontWeight: 400 }}>
            {helper}
          </span>
        )}
      </label>
      <input
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        readOnly={readOnly}
        style={readOnly ? { ...inputStyle, background: '#F8FAFC', cursor: 'default' } : inputStyle}
      />
    </div>
  );
}

interface SelectFieldProps {
  label: string;
  value?: string;
  options: SelectOption[];
  onChange: (v: string) => void;
  required?: boolean;
}

function SelectField({ label, value, options, onChange, required }: SelectFieldProps) {
  return (
    <div>
      <label style={labelStyle}>
        {label}
        {required && <span style={{ color: '#DC2626' }}> *</span>}
      </label>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...inputStyle, background: '#fff' }}
      >
        <option value="">--</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
